/**
 * Desktop Header Component
 * Top bar with breadcrumbs, page title, and user menu
 */
import React, { useState, useContext } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { AuthContext } from '../../../Context/AuthContext';
import { DesktopTheme } from '../theme/desktopTheme';

// Page title mapping
const PAGE_TITLES = {
  'ESG Pillars': 'ESG Dashboard',
  'Contributors': 'Team Contributors',
  'Dept Performance': 'Department Performance',
  'Report': 'All Responses',
  'Improvement Report': 'Performance Trends',
  'Remaining Topics': 'Topic Assignment',
  'Feedback & Support': 'Feedback & Support',
};

const PAGE_DESCRIPTIONS = {
  'ESG Pillars': 'Overview of Environmental, Social, and Governance scores',
  'Contributors': 'Manage and view employee contributions',
  'Dept Performance': 'Department-wise ESG performance breakdown',
  'Report': 'View all questionnaire responses',
  'Improvement Report': 'Track quarter-over-quarter improvements',
  'Remaining Topics': 'Assign topics to employees',
  'Feedback & Support': 'Send feedback or get support',
};

const Header = ({ currentRoute, quarterSelector }) => {
  const navigation = useNavigation();
  const { companyName, logoutCompany } = useContext(AuthContext);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isUserMenuHovered, setIsUserMenuHovered] = useState(false);

  const pageTitle = PAGE_TITLES[currentRoute] || currentRoute;
  const pageDescription = PAGE_DESCRIPTIONS[currentRoute] || '';

  const handleLogout = async () => {
    await logoutCompany();
    navigation.reset({
      index: 0,
      routes: [{ name: 'CompanyLoginScreen' }],
    });
  };

  // Get initials from company name
  const getInitials = (name) => {
    if (!name) return 'CO';
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <View style={styles.header}>
      {/* Left Section - Page Info */}
      <View style={styles.leftSection}>
        <View style={styles.titleContainer}>
          <Text style={styles.pageTitle}>{pageTitle}</Text>
          {pageDescription ? (
            <Text style={styles.pageDescription}>{pageDescription}</Text>
          ) : null}
        </View>
      </View>

      {/* Right Section - Quarter Selector & User Menu */}
      <View style={styles.rightSection}>
        {/* Quarter selector slot (passed as prop) */}
        {quarterSelector && (
          <View style={styles.quarterContainer}>{quarterSelector}</View>
        )}

        {/* User Menu */}
        <View style={styles.userMenuContainer}>
          <TouchableOpacity
            style={[
              styles.userButton,
              isUserMenuHovered && styles.userButtonHover,
            ]}
            onPress={() => setIsUserMenuOpen(!isUserMenuOpen)}
            onMouseEnter={
              Platform.OS === 'web'
                ? () => setIsUserMenuHovered(true)
                : undefined
            }
            onMouseLeave={
              Platform.OS === 'web'
                ? () => setIsUserMenuHovered(false)
                : undefined
            }
          >
            <View style={styles.avatar}>
              <Text style={styles.avatarText}>{getInitials(companyName)}</Text>
            </View>
            <View style={styles.userInfo}>
              <Text style={styles.userName} numberOfLines={1}>
                {companyName || 'Company'}
              </Text>
              <Text style={styles.userRole}>Administrator</Text>
            </View>
            <Text style={styles.chevron}>▼</Text>
          </TouchableOpacity>

          {/* Dropdown Menu */}
          {isUserMenuOpen && (
            <View style={styles.dropdown}>
              <TouchableOpacity
                style={styles.dropdownItem}
                onPress={() => {
                  setIsUserMenuOpen(false);
                  navigation.navigate('Feedback & Support');
                }}
              >
                <Text style={styles.dropdownIcon}>⚙️</Text>
                <Text style={styles.dropdownText}>Settings</Text>
              </TouchableOpacity>
              <View style={styles.dropdownDivider} />
              <TouchableOpacity
                style={[styles.dropdownItem, styles.dropdownItemDanger]}
                onPress={() => {
                  setIsUserMenuOpen(false);
                  handleLogout();
                }}
              >
                <Text style={styles.dropdownIcon}>🚪</Text>
                <Text style={[styles.dropdownText, styles.dropdownTextDanger]}>
                  Logout
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

const { colors, typography, spacing, layout, borderRadius, shadows } =
  DesktopTheme;

const styles = StyleSheet.create({
  header: {
    height: layout.headerHeight,
    backgroundColor: colors.headerBg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.xl,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    ...shadows.sm,
  },

  // Left Section
  leftSection: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  titleContainer: {
    flex: 1,
  },
  pageTitle: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.h3,
    color: colors.textPrimary,
  },
  pageDescription: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.small,
    color: colors.textSecondary,
    marginTop: 2,
  },

  // Right Section
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  quarterContainer: {
    marginRight: spacing.lg,
  },

  // User Menu
  userMenuContainer: {
    position: 'relative',
  },
  userButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: borderRadius.md,
    backgroundColor: 'transparent',
  },
  userButtonHover: {
    backgroundColor: colors.contentBg,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.small,
    color: '#fff',
  },
  userInfo: {
    marginLeft: spacing.sm,
    marginRight: spacing.sm,
  },
  userName: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.body,
    color: colors.textPrimary,
    maxWidth: 120,
  },
  userRole: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.tiny,
    color: colors.textSecondary,
  },
  chevron: {
    fontSize: 10,
    color: colors.textSecondary,
  },

  // Dropdown
  dropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: spacing.xs,
    backgroundColor: colors.cardBg,
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    minWidth: 180,
    ...shadows.lg,
    zIndex: 1000,
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm + 4,
    paddingHorizontal: spacing.md,
  },
  dropdownItemDanger: {},
  dropdownIcon: {
    fontSize: 16,
    marginRight: spacing.sm,
  },
  dropdownText: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.textPrimary,
  },
  dropdownTextDanger: {
    color: colors.error,
  },
  dropdownDivider: {
    height: 1,
    backgroundColor: colors.border,
    marginHorizontal: spacing.sm,
  },
});

export default Header;
